/**
 * coverScaleBar — derive a real, parcel-sized scale bar for the
 * BoundaryPlateCover engraver's plate.
 *
 * The legacy plate drew a HARD-CODED "0 … 1 mi" bar with four fixed
 * segments and only two tick labels (0 and 1) — the filled segments ran
 * past the "1 mi" tick into unlabelled space, and 1 mile is far too
 * coarse for a 17–187 ac parcel (which is ~250–900 m across). This
 * helper instead:
 *
 *   1. measures the TRUE parcel ground extent (max of bbox width/height
 *      in metres, via haversine — same geometry the overlay draws);
 *   2. picks a "nice" 1-2-5 round total distance ≈ that extent;
 *   3. splits it into N equal segments so the segment-draw loop and the
 *      tick-label loop share ONE source of truth (no drift);
 *   4. labels EVERY tick (0 … total) in sensible units (ft for small
 *      parcels, mi once the bar crosses ~¼ mile).
 *
 * Pure + side-effect-free so it unit-tests in isolation and so the cover
 * stays presentational.
 */
import type { SiteRagReport } from '../../../../types/siteRag';
import {
  materializeSiteBoundaries,
  aggregateBbox,
  haversineMeters,
} from '../../../../lib/siteBoundaryGeometry';

const FEET_PER_METER = 3.280839895;
const FEET_PER_MILE = 5280;
const SEGMENT_COUNT = 4;

/** A single rendered tick: its 0-based index, the cumulative distance it
 *  marks, and the formatted label (only the last tick carries the unit). */
export interface ScaleBarTick {
  index: number;
  /** Distance from the bar's origin in the chosen display unit. */
  value: number;
  /** Formatted label, e.g. "0", "250", "500 ft" / "0", "¼", "½ mi". */
  label: string;
}

export interface ScaleBarModel {
  /** Equal segments; each alternates filled/empty in the draw loop. */
  segmentCount: number;
  /** SEGMENT_COUNT + 1 ticks (0 … total), every one labelled. */
  ticks: ScaleBarTick[];
  /** Unit suffix for the trailing tick ("ft" | "mi"). */
  unit: 'ft' | 'mi';
  /** Ground distance the WHOLE bar represents, in metres (for tests). */
  totalMeters: number;
}

/** Round `x` UP to the nearest 1-2-5 × 10ⁿ value (a "nice" axis step). */
function niceCeil(x: number): number {
  if (!(x > 0)) return 0;
  const exp = Math.floor(Math.log10(x));
  const base = Math.pow(10, exp);
  const frac = x / base;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * base;
}

/** Parcel ground extent in metres — the larger of bbox width / height.
 *  `null` when the report carries no drawable parcel polygon. */
export function parcelExtentMeters(report: SiteRagReport): number | null {
  const parcels = materializeSiteBoundaries({
    siteBoundaries: report.siteBoundaries,
    siteBoundary: report.siteBoundary,
    siteArea: report.siteArea,
    sitePerimeter: report.sitePerimeter,
    siteCentroid: report.siteCentroid,
  });
  if (parcels.length === 0) return null;
  const bbox = aggregateBbox(parcels);
  const midLat = (bbox.north + bbox.south) / 2;
  // Width measured along the parcel's mid-latitude; height N→S.
  const widthM = haversineMeters(
    { lat: midLat, lng: bbox.west },
    { lat: midLat, lng: bbox.east },
  );
  const heightM = haversineMeters(
    { lat: bbox.south, lng: bbox.west },
    { lat: bbox.north, lng: bbox.west },
  );
  const extent = Math.max(widthM, heightM);
  return Number.isFinite(extent) && extent > 0 ? extent : null;
}

/** Format a tick value for display. Feet print as integers with
 *  thousands separators; miles print with up to one decimal (trailing
 *  zero trimmed), since parcel-scale mile bars step in 0.1–0.5 mi. */
function formatTick(value: number, unit: 'ft' | 'mi'): string {
  if (unit === 'mi') {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }
  return Math.round(value).toLocaleString('en-US');
}

/** Above this foot-step the bar switches to miles so labels stay short.
 *  At/below it, feet read cleaner than fractional miles for a parcel
 *  (a 187 ac parcel is ~2 800 ft across — still firmly in feet). */
const MAX_FOOT_STEP = 1000;

/**
 * Build a parcel-sized scale-bar model. Falls back to a sensible default
 * (1000 ft total) when the report has no geometry, so the bar never
 * renders blank — but callers that hide the bar when geometry is absent
 * can simply check `parcelExtentMeters` first.
 */
export function computeScaleBar(report: SiteRagReport): ScaleBarModel {
  const extentM = parcelExtentMeters(report) ?? 304.8; // 1000 ft default
  // Aim for the bar to span ~80 % of the parcel extent so the parcel
  // reads as "a few bar-lengths" wide rather than the bar dwarfing it.
  const targetM = extentM * 0.8;

  // Compute the nice per-segment step in FEET first. If that step is
  // small enough, feet are the legible unit; otherwise convert the metre
  // target to a nice mile step. Either way the segment-draw loop and the
  // tick loop both read the SAME `ticks` model, so they cannot drift.
  const footStep = niceCeil((targetM * FEET_PER_METER) / SEGMENT_COUNT) || 100;

  let unit: 'ft' | 'mi';
  let step: number;
  if (footStep <= MAX_FOOT_STEP) {
    unit = 'ft';
    step = footStep;
  } else {
    unit = 'mi';
    const targetMiles = targetM / (FEET_PER_MILE / FEET_PER_METER);
    step = niceCeil(targetMiles / SEGMENT_COUNT) || 0.25;
  }
  const total = step * SEGMENT_COUNT;

  const ticks: ScaleBarTick[] = [];
  for (let i = 0; i <= SEGMENT_COUNT; i++) {
    const value = step * i;
    const isLast = i === SEGMENT_COUNT;
    const base = formatTick(value, unit);
    ticks.push({
      index: i,
      value,
      label: isLast ? `${base} ${unit}` : base,
    });
  }

  const totalMeters = unit === 'mi'
    ? total * (FEET_PER_MILE / FEET_PER_METER)
    : total / FEET_PER_METER;

  return { segmentCount: SEGMENT_COUNT, ticks, unit, totalMeters };
}
